import { isBillableCodeShape } from '@utils/billingCode';

import type { GeneratedIntakeField, IntakeFieldValue } from './types';

function normalizedKey(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase().replaceAll('.', '_');
}

function fieldKeys(field: GeneratedIntakeField): Set<string> {
  return new Set([normalizedKey(field.field_key), normalizedKey(field.destination.field_key)]);
}

function hasKey(keys: Set<string>, ...expected: string[]): boolean {
  return expected.some(key => keys.has(key));
}

function isExpandedPart(
  field: GeneratedIntakeField,
  keys: Set<string>,
  part: 'email' | 'fax' | 'line_1' | 'phone' | 'zip',
): boolean {
  const parentKey = normalizedKey(field.parent_field_key);
  return Boolean(parentKey) && [...keys].some(key => key === `${parentKey}_${part}`);
}

/**
 * Matches a field whose normalized key is exactly `suffix`, or ends with
 * `_${suffix}` (e.g. "reference_phone", "billing_contact_phone"). Used so
 * any current or future `*_phone` / `*_fax` catalog field is covered without
 * needing to be named explicitly.
 */
function matchesKeySuffix(keys: Set<string>, suffix: string): boolean {
  return [...keys].some(key => key === suffix || key.endsWith(`_${suffix}`));
}

/** Any phone-shaped field: exact key "phone", any "*_phone" key, or a template-expanded phone part. */
export function isPhoneField(field: GeneratedIntakeField): boolean {
  const keys = fieldKeys(field);
  return matchesKeySuffix(keys, 'phone') || isExpandedPart(field, keys, 'phone');
}

/** Any fax-shaped field: exact key "fax", any "*_fax" key, or a template-expanded fax part. */
export function isFaxField(field: GeneratedIntakeField): boolean {
  const keys = fieldKeys(field);
  return matchesKeySuffix(keys, 'fax') || isExpandedPart(field, keys, 'fax');
}

/** Any field that should be treated as a phone/fax number for input filtering and validation. */
export function isPhoneOrFaxField(field: GeneratedIntakeField): boolean {
  return isPhoneField(field) || isFaxField(field);
}

function isBlank(value: IntakeFieldValue): boolean {
  if (Array.isArray(value)) return value.length === 0 || value.every(entry => entry.trim() === '');
  return typeof value === 'string' && value.trim() === '';
}

function stringValue(value: IntakeFieldValue): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function entries(value: IntakeFieldValue): string[] {
  if (Array.isArray(value)) return value.map(entry => entry.trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  return value
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean);
}

/**
 * MVP-only local format checks for the generated Intake Hub fields named in
 * GDN-3209. Messages describe the expected shape and never include the typed
 * value, which is especially important for SSN and bank details.
 */
export function getIntakeFieldFormatError(
  field: GeneratedIntakeField,
  value: IntakeFieldValue,
  acknowledgedValue?: IntakeFieldValue,
): string | undefined {
  if (field.destination.kind === 'intake_item_sensitive' && value === true) return undefined;
  if (isBlank(value)) return undefined;

  const keys = fieldKeys(field);
  const text = stringValue(value);

  if (hasKey(keys, 'npi_type1', 'npi_type2', 'supervisor_npi')) {
    return /^\d{10}$/.test(text) ? undefined : 'Enter exactly 10 digits.';
  }

  if (hasKey(keys, 'ein')) {
    return /^\d{2}-?\d{7}$/.test(text) ? undefined : 'Enter 9 digits, with an optional hyphen.';
  }

  if (hasKey(keys, 'ssn', 'bank_routing_number')) {
    const digits = text.replace(/[\s-]/g, '');
    return /^\d{9}$/.test(digits) ? undefined : 'Enter exactly 9 digits.';
  }

  if (hasKey(keys, 'dea_number')) {
    return /^[A-Za-z]{2}\d{7}$/.test(text) ? undefined : 'Enter 2 letters followed by 7 digits.';
  }

  if (hasKey(keys, 'common_cpt_codes')) {
    const codes = entries(value);
    const acknowledgedCodes = new Set(
      entries(acknowledgedValue ?? '').map(code => code.toUpperCase()),
    );
    return codes.length > 0 &&
      codes.every(code => isBillableCodeShape(code) || acknowledgedCodes.has(code.toUpperCase()))
      ? undefined
      : 'Select a valid CPT or HCPCS code.';
  }

  if (hasKey(keys, 'zip') || isExpandedPart(field, keys, 'zip')) {
    return /^\d{5}(-\d{4})?$/.test(text) ? undefined : 'Enter a 5-digit ZIP code or ZIP+4.';
  }

  if (
    hasKey(keys, 'email', 'reference_email', 'office_email') ||
    isExpandedPart(field, keys, 'email')
  ) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text)
      ? undefined
      : 'Enter an email address in the format name@example.com.';
  }

  if (isPhoneField(field)) {
    const digits = text.replace(/[\s().-]/g, '');
    return /^\d{10}$/.test(digits) ? undefined : 'Enter a 10-digit US phone number.';
  }

  if (isFaxField(field)) {
    const digits = text.replace(/[\s().-]/g, '');
    return /^\d{10}$/.test(digits) ? undefined : 'Enter a 10-digit US fax number.';
  }

  if (hasKey(keys, 'caqh_id')) {
    return /^\d{8}$/.test(text) ? undefined : 'Enter exactly 8 digits.';
  }

  if (field.data_type.toLowerCase() === 'date') {
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? undefined : 'Enter a date in YYYY-MM-DD format.';
  }

  const isPracticeLocationLineOne =
    normalizedKey(field.parent_field_key) === 'location_address' &&
    (isExpandedPart(field, keys, 'line_1') || keys.has('location_address_line_1'));
  if (isPracticeLocationLineOne && /\bP\.?\s*O\.?\s*Box\b/i.test(text)) {
    return 'Enter a physical street address, not a PO Box.';
  }

  return undefined;
}
